if (!customElements.get('crystallode-variant-selector')) {
  customElements.define('crystallode-variant-selector', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-selector-dialog]');
      this.variants = JSON.parse(this.querySelector('[data-variant-data]')?.textContent || '[]');
      this.optionNames = JSON.parse(this.querySelector('[data-option-data]')?.textContent || '[]');
      this.pageSize = Number(this.dataset.pageSize || 20);
      this.page = 1;
      this.selected = new Map();
      this.search = '';
      this.filters = {};
      this.currency = this.dataset.currency || 'USD';
      this.locale = this.dataset.locale || 'en';
      this.labels = {
        all: this.dataset.labelAll,
        select: this.dataset.labelSelect,
        selected: this.dataset.labelSelected,
        unavailable: this.dataset.labelUnavailable,
        noResults: this.dataset.labelNoResults,
        noSelection: this.dataset.labelNoSelection,
        item: this.dataset.labelItem,
        items: this.dataset.labelItems,
        addError: this.dataset.labelAddError
      };
      this.bind();
      this.buildFilters();
      this.render();
      this.relocateTrigger();
    }

    // 第 6 项：把「选择变体」按钮挪到产品页价格下面。
    //
    // Shopify 的 section 不能嵌进另一个 section，所以整块 crystallode-variant-selector
    // 没法塞进 main-product 内部。但那个变体抽屉是 <dialog>，showModal() 时会渲染在
    // top-layer，浮在整个页面之上 —— 它在 HTML 里的位置根本不影响视觉。
    // 所以这里只把触发按钮搬到价格块后面，弹窗本体留在页面底部不动，
    // 改动量从「重构 4 个文件 + 4 个模板 JSON」降到「搬一个按钮」。
    //
    // 任何一步找不到目标都直接 return，按钮留在原地，不会把页面搞坏。
    relocateTrigger() {
      const trigger = this.querySelector('[data-open-selector]');
      if (!trigger) return;

      const container = document.querySelector('.product__info-container');
      if (!container) return;

      const price = container.querySelector('.price');
      if (!price) return;

      // 价格组件可能被 block 包了好几层，向上找到它在 info-container 下的那个直接子元素，
      // 这样不依赖主题给 block 起的具体 class 名（Trade 升级时最容易变的就是这个）。
      let anchor = price;
      while (anchor && anchor.parentElement !== container) {
        anchor = anchor.parentElement;
      }
      if (!anchor) return;

      const holder = document.createElement('div');
      holder.className = 'cw-variant-selector__relocated';
      holder.style.margin = '1.2rem 0';
      trigger.style.width = '100%';
      holder.appendChild(trigger);
      anchor.insertAdjacentElement('afterend', holder);

      // 多变体产品由这个抽屉接管加购，所以把主题原生的购买区收起来。
      //
      // 原因：templates/product.json 里 hide_variants 是 true（变体选择器被隐藏，因为
      // 改用了自建的变体抽屉）。这种情况下原生的 Add to cart 只会把「第一个变体」
      // 加进购物车，客户以为自己买的是想要的规格 —— 批发单里这是事故。
      //
      // 这个 section 本身只在 product.variants.size > 1 时才渲染，
      // 所以单变体产品根本不走这段，它们的原生 Add to cart 会完整保留。
      container.querySelectorAll('product-form').forEach((form) => {
        form.style.display = 'none';
      });

      // 按钮搬走后，页面底部原来那块「标题 + 说明 + 按钮」只剩标题和说明，
      // 和价格下方的按钮重复，收起来更干净。dialog 仍留在这个 section 里，
      // 所以只能隐藏 summary，不能隐藏整个 section —— 祖先 display:none 会让
      // showModal() 弹不出来。
      const summary = this.querySelector('.cw-variant-selector__summary');
      if (summary) summary.style.display = 'none';

      const section = this.closest('.cw-variant-section');
      if (section) {
        section.style.paddingTop = '0';
        section.style.paddingBottom = '0';
      }
    }

    bind() {
      this.querySelector('[data-open-selector]')?.addEventListener('click', () => this.dialog?.showModal());
      this.querySelector('[data-close-selector]')?.addEventListener('click', () => this.dialog?.close());
      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.querySelector('[data-variant-search]')?.addEventListener('input', (event) => {
        this.search = event.target.value.trim().toLowerCase();
        this.page = 1;
        this.render();
      });
      this.querySelector('[data-variant-previous]')?.addEventListener('click', () => {
        this.page = Math.max(1, this.page - 1);
        this.render();
      });
      this.querySelector('[data-variant-next]')?.addEventListener('click', () => {
        this.page += 1;
        this.render();
      });
      this.querySelector('[data-add-selected]')?.addEventListener('click', () => this.addSelected());
    }

    buildFilters() {
      const names = this.optionNames;
      const container = this.querySelector('[data-option-filters]');
      names.forEach((name, index) => {
        const values = [...new Set(this.variants.map((variant) => variant.options?.[index]).filter(Boolean))];
        if (values.length < 2 || values.length > 60) return;
        const label = document.createElement('label');
        label.innerHTML = `<span>${this.escape(name)}</span><select><option value="">${this.escape(this.labels.all)}</option>${values.map((value) => `<option value="${this.escape(value)}">${this.escape(value)}</option>`).join('')}</select>`;
        label.querySelector('select').addEventListener('change', (event) => {
          this.filters[index] = event.target.value;
          this.page = 1;
          this.render();
        });
        container.append(label);
      });
    }

    get filteredVariants() {
      return this.variants.filter((variant) => {
        const haystack = `${variant.title} ${variant.sku || ''}`.toLowerCase();
        if (this.search && !haystack.includes(this.search)) return false;
        return Object.entries(this.filters).every(([index, value]) => !value || variant.options?.[Number(index)] === value);
      });
    }

    render() {
      const filtered = this.filteredVariants;
      const pages = Math.max(1, Math.ceil(filtered.length / this.pageSize));
      this.page = Math.min(this.page, pages);
      const start = (this.page - 1) * this.pageSize;
      const visible = filtered.slice(start, start + this.pageSize);
      const results = this.querySelector('[data-variant-results]');
      results.innerHTML = visible.length ? visible.map((variant) => {
        const chosen = this.selected.has(String(variant.id));
        return `<div class="cw-variant-row"><div class="cw-variant-row__title"><strong>${this.escape(variant.title)}</strong><small>${variant.sku ? `SKU: ${this.escape(variant.sku)}` : ''}</small></div><span class="cw-variant-row__price">${this.money(variant.price)}</span><button type="button" data-select-variant="${variant.id}" aria-pressed="${chosen}" ${variant.available ? '' : 'disabled'}>${chosen ? this.escape(this.labels.selected) : variant.available ? this.escape(this.labels.select) : this.escape(this.labels.unavailable)}</button></div>`;
      }).join('') : `<p>${this.escape(this.labels.noResults)}</p>`;
      results.querySelectorAll('[data-select-variant]').forEach((button) => button.addEventListener('click', () => this.toggle(button.dataset.selectVariant)));
      this.querySelector('[data-variant-page]').textContent = `${this.page} / ${pages} · ${filtered.length}`;
      this.querySelector('[data-variant-previous]').disabled = this.page <= 1;
      this.querySelector('[data-variant-next]').disabled = this.page >= pages;
      this.renderSelected();
    }

    toggle(id) {
      const key = String(id);
      if (this.selected.has(key)) this.selected.delete(key);
      else {
        const variant = this.variants.find((item) => String(item.id) === key);
        if (variant) this.selected.set(key, { variant, quantity: 1 });
      }
      this.render();
    }

    renderSelected() {
      const container = this.querySelector('[data-selected-variants]');
      const entries = [...this.selected.entries()];
      container.innerHTML = entries.length ? entries.map(([id, item]) => `<label class="cw-selected-variant"><span><strong>${this.escape(item.variant.title)}</strong><small>${this.money(item.variant.price)}</small></span><input type="number" min="1" step="1" value="${item.quantity}" data-selected-quantity="${id}" aria-label="${this.escape(item.variant.title)}"></label>`).join('') : `<p>${this.escape(this.labels.noSelection)}</p>`;
      container.querySelectorAll('[data-selected-quantity]').forEach((input) => input.addEventListener('change', () => {
        const item = this.selected.get(input.dataset.selectedQuantity);
        if (item) item.quantity = Math.max(1, Number(input.value || 1));
        this.renderTotals();
      }));
      this.renderTotals();
    }

    renderTotals() {
      const entries = [...this.selected.values()];
      const quantity = entries.reduce((sum, item) => sum + item.quantity, 0);
      const total = entries.reduce((sum, item) => sum + (Number(item.variant.price) * item.quantity), 0);
      this.querySelector('[data-selection-count]').textContent = `${quantity} ${quantity === 1 ? this.labels.item : this.labels.items}`;
      this.querySelector('[data-selection-total]').textContent = this.money(total);
      this.querySelector('[data-add-selected]').disabled = entries.length === 0;
    }

    async addSelected() {
      const button = this.querySelector('[data-add-selected]');
      const items = [...this.selected.values()].map((item) => ({ id: item.variant.id, quantity: item.quantity }));
      if (!items.length) return;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) throw new Error(await response.text());
        window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
      } catch (error) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        window.alert(this.labels.addError);
      }
    }

    money(cents) {
      return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency }).format(Number(cents || 0) / 100);
    }

    // 结果会被插进 HTML 属性值（value="..."、aria-label="..."），
    // 所以必须连引号一起转义。原来用 textContent + innerHTML 只转义 < > &，
    // 变体标题里带英文双引号时（水晶尺寸常写成 8" Amethyst Tower）会提前闭合属性。
    escape(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  });
}
